import axios, { AxiosError, AxiosRequestConfig, AxiosResponse, InternalAxiosRequestConfig } from "axios";
import find from "lodash/find";
import { getSettings, updateSettings } from "./settingsService";
import { addHours, getDefaultStartDate } from "../common/dateOperations";
import IProject from "../interfaces/IProject";
import IReport from "../interfaces/IReport";
import IDashboard from "../interfaces/IDashboard";
import ICustomer from "../interfaces/ICustomer";
import IUpdateProject from "../interfaces/IUpdateProject";
import ICloseProjectData from "../interfaces/ICloseProjectData";
import ISettings from "../interfaces/ISettings";
import IUser from "../interfaces/IUser";
import ITask from "../interfaces/ITask";
import IComment from "../interfaces/IComment";
import IMe from "../interfaces/IMe";
import ITool from "../interfaces/ITool";
import { IPrompt } from "../interfaces/IPrompt";
import { isTokenExpiring, refreshAccessToken } from "./tokenService";

const baseUrl = import.meta.env.VITE_REPORT_SERVICE;

const getRequestConfig = (accessToken: string): AxiosRequestConfig => {
    return {
        headers: {
            "Accept": "application/json",
            "Content-Type": "application/json",
            "Authorization": `Bearer ${accessToken}`
        }
    };
};

const api = axios.create();

type RetriableRequestConfig = InternalAxiosRequestConfig & { retriedWithNewToken?: boolean };

// The token may have expired while the app was in the background, renew it before the request goes out
api.interceptors.request.use(async (config: InternalAxiosRequestConfig) => {
    if (isTokenExpiring()) {
        const accessToken = await refreshAccessToken();
        if (accessToken) {
            config.headers.set("Authorization", `Bearer ${accessToken}`);
        }
    }

    return config;
});

// The API rejected the token anyway, renew it and repeat the request once
api.interceptors.response.use(undefined, async (error: AxiosError) => {
    const config = error.config as RetriableRequestConfig | undefined;
    if (error.response?.status !== 401 || !config || config.retriedWithNewToken) {
        throw error;
    }

    config.retriedWithNewToken = true;
    const accessToken = await refreshAccessToken();
    if (!accessToken) {
        throw error;
    }

    config.headers.set("Authorization", `Bearer ${accessToken}`);
    return api.request(config);
});

const get = async (accessToken: string, url: string) => {
    const config = getRequestConfig(accessToken);
    return await api.get(`${baseUrl}${url}`, config);
};

const post = async (accessToken: string, url: string, data: any) => {
    const config = getRequestConfig(accessToken);
    return await api.post(`${baseUrl}${url}`, data, config);
};

const patch = async (accessToken: string, url: string, data: any) => {
    const config = getRequestConfig(accessToken);
    return await api.patch(`${baseUrl}${url}`, data, config);
};

const del = async (accessToken: string, url: string) => {
    const config = getRequestConfig(accessToken);
    return await api.delete(`${baseUrl}${url}`, config);
};

export const getMe = async (accessToken: string): Promise<IMe> => {
    const resp = await get(accessToken, `/api/users/me`);
    return resp.data;
};

export const createReport = async (accessToken: string, data: IReport, forceSyncWithOutlook: boolean = false): Promise<AxiosResponse> => {
    const { syncWithOutlook } = getSettings();
    return await post(accessToken, `/api/reports?syncWithOutlook=${syncWithOutlook || forceSyncWithOutlook}`, data);
};

export const updateReport = async (accessToken: string, data: any): Promise<AxiosResponse> => {
    return await patch(accessToken, "/api/reports", data);
};

export const getDashboard = async (accessToken: string, startDate: Date): Promise<IDashboard> => {
    const resp = await get(accessToken, `/api/dashboard?startDate=${startDate.toISOString().replace("Z", "")}`);
    return resp.data;
};

export const getDashboardMonth = async (accessToken: string, startDate: Date): Promise<number> => {
    const resp = await get(accessToken, `/api/dashboard/getmonth?startDate=${startDate.toISOString()}`);
    return resp.data;
};

export const getProjects = async (accessToken: string): Promise<IProject[]> => {
    const resp = await get(accessToken, "/api/projects");
    return resp.data;
};

export const getProjectsWithDetails = async (accessToken: string, viewId: number = 0): Promise<IProject[]> => {
    const resp = await get(accessToken, `/api/projects/details?viewId=${viewId}`);
    return resp.data;
};

export const getProjectWithDetails = async (accessToken: string, projectId: string): Promise<IProject> => {
    const resp = await get(accessToken, `/api/projects/details/${projectId}`);
    return resp.data;
};

export const updateProject = async (accessToken: string, id: string, data: IUpdateProject, includeEmptyValues: boolean): Promise<void> => {
    await patch(accessToken, `/api/projects/${id}?includeEmptyValues=${includeEmptyValues}`, data);
};

// The dialog that edits a project has to hand the updated project back to the window that opened it
export const mergeUpdatedProject = (projects: IProject[], id: string, data: IUpdateProject): IProject => {
    const project = find(projects, (x: IProject) => x.id === id);
    if (!project) {
        throw new Error("Případ nebyl nalezen v mezipaměti.");
    }

    const plannedHours = project.plannedHours - project.remainingHours + (data.remainingHours ?? 0);
    return {
        ...project,
        dueDate: data.dueDate,
        remainingHours: data.remainingHours ?? 0,
        plannedHours
    };
};

export const openProject = async (accessToken: string, id: string): Promise<void> => {
    await post(accessToken, `/api/projects/${id}/open`, null);
}

export const resolveProject = async (accessToken: string, id: string, data: ICloseProjectData): Promise<void> => {
    await post(accessToken, `/api/projects/${id}/resolve`, data);
}

export const changeProjectOwner = async (accessToken: string, projectId: string, userId: string): Promise<void> => {
    await post(accessToken, `/api/projects/${projectId}/owner/${userId}`, null);
};

export const getReports = async (accessToken: string, numberOfWeeksToFetch: number): Promise<IReport[]> => {
    const resp = await get(accessToken, `/api/reports?numberOfWeeksToFetch=${numberOfWeeksToFetch}`);
    return resp.data;
};

export const getCustomers = async (accessToken: string): Promise<ICustomer[]> => {
    const resp = await get(accessToken, "/api/customers");
    return resp.data;
};

export const setClientSettings = async (accessToken: string, data: ISettings): Promise<void> => {
    updateSettings(data);
    await post(accessToken, "/api/clientsettings", data);
}

export const getUsers = async (accessToken: string): Promise<IUser[]> => {
    const resp = await get(accessToken, "/api/users");
    return resp.data;
};

export const getTask = async (accessToken: string, taskId: string): Promise<ITask> => {
    const resp = await get(accessToken, `/api/tasks/${taskId}`);
    return resp.data;
};

export const getTasks = async (accessToken: string, viewId: number): Promise<ITask[]> => {
    const resp = await get(accessToken, `/api/tasks?viewId=${viewId}`);
    return resp.data;
};

export const completeTask = async (accessToken: string, id: string): Promise<void> => {
    await post(accessToken, `/api/tasks/${id}/complete`, null);
}

export const cancelTask = async (accessToken: string, id: string): Promise<void> => {
    await post(accessToken, `/api/tasks/${id}/cancel`, null);
}

export const upsertTask = async (accessToken: string, data: ITask): Promise<string> => {
    const resp = await post(accessToken, "/api/tasks", data);
    return resp.data;
}

export const addComment = async (accessToken: string, projectId: string, data: IComment): Promise<void> => {
    await post(accessToken, `/api/projects/${projectId}/comment`, data);
}

export const deleteReport = async (accessToken: string, reportId: string): Promise<AxiosResponse> => {
    return await del(accessToken, `/api/reports/${reportId}`);
};

export const getTools = async (accessToken: string): Promise<ITool[]> => {
    const resp = await get(accessToken, "/api/tools");
    return resp.data;
};

export const aiPrompt = async (accessToken: string, data: IPrompt): Promise<string> => {
    const resp = await post(accessToken, "/api/prompt", data);
    return resp.data;
};

export const getEmptyReport = (): IReport => {
    return {
        id: "",
        carpool: false,
        durationInMinutes: 0,
        end: addHours(getDefaultStartDate(), 0.5),
        link: "",
        overhead: false,
        overtime: false,
        extendedHours: false,
        project: {
            id: "",
            parentIsClosed: false,
            plannedHours: 0,
            remainingHours: 0,
            reportedHours: 0,
            subject: "",
            customerId: "",
            number: "",
            owner: {
                login: "",
                viewOnlyOwnReports: false
            }
        },
        requestedByCustomer: false,
        shadowing: false,
        start: getDefaultStartDate(),
        state: 0,
        subject: ""
    };
};

export const getEmptyDashboard = (): IDashboard => {
    return {
        monday: 0,
        tuesday: 0,
        wednesday: 0,
        thursday: 0,
        friday: 0,
        saturday: 0,
        sunday: 0,
        sum: 0
    };
};
